import { Link } from 'react-router-dom';
import { ArrowRight, Users } from 'lucide-react';

import { EventLifecycleButtons } from '@/components/events/EventLifecycleButtons';
import { EventStatusBadge } from '@/components/events/EventStatusBadge';
import type { Event } from '@/config/objects/events';

export interface DashboardEventRowProps {
  event: Event;
  /** Right-hand meta line, e.g. "Started 2h ago" or "Starts Jul 12, 9:00 AM". */
  meta?: string;
}

/**
 * One event row on the dashboard: status, name, team count, meta line, and
 * the primary lifecycle action. Single line on desktop; on phones the
 * identity (badge + name + meta) takes the full width and the actions drop
 * to their own right-aligned row so nothing truncates or wraps mid-word.
 */
export function DashboardEventRow({ event, meta }: DashboardEventRowProps) {
  return (
    <div className="flex flex-col gap-2 px-4 py-3.5 transition-colors hover:bg-muted/50 sm:flex-row sm:items-center sm:gap-4 sm:px-5">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <EventStatusBadge status={event.status} />
        <div className="min-w-0 flex-1">
          <Link
            to={`/events/${event.id}`}
            className="block truncate font-medium text-foreground hover:text-primary"
          >
            {event.name}
          </Link>
          <div className="mt-0.5 flex items-center gap-3 font-mono text-[0.6875rem] tracking-wide text-muted-foreground">
            {typeof event._count?.teams === 'number' ? (
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <Users className="h-3 w-3" aria-hidden="true" />
                {event._count.teams} team{event._count.teams === 1 ? '' : 's'}
              </span>
            ) : null}
            {meta ? <span className="truncate whitespace-nowrap">{meta}</span> : null}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center justify-end gap-3">
        <EventLifecycleButtons event={event} limit={1} size="sm" />
        <Link
          to={`/events/${event.id}`}
          aria-label={`Open ${event.name}`}
          className="text-muted-foreground transition-colors hover:text-primary"
        >
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
